import type { ReactNode } from 'react';
import { FormMessage } from './FormMessage';
import { useFieldA11y } from './fieldA11y';

interface SegmentedControlOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SegmentedControlProps {
  label: ReactNode;
  value: string;
  options: SegmentedControlOption[];
  onValueChange: (value: string) => void;
  helperText?: string;
  error?: string;
  className?: string;
}

export const SegmentedControl = ({
  label,
  value,
  options,
  onValueChange,
  helperText,
  error,
  className,
}: SegmentedControlProps) => {
  const { fieldId, helperId, errorId, describedByIds } = useFieldA11y({
    helperText,
    error,
  });

  return (
    <fieldset
      className={[
        'form-segmented',
        error ? 'form-segmented--invalid' : '',
        className,
      ]
        .filter(Boolean)
      .join(' ')}
      aria-describedby={describedByIds}
    >
      <legend className="form-field__label" id={fieldId}>
        {label}
      </legend>
      <div className="form-segmented__options" role="group" aria-labelledby={fieldId}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={option.value === value ? 'is-active' : undefined}
            aria-pressed={option.value === value}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <FormMessage id={helperId}>{helperText}</FormMessage>
      <FormMessage id={errorId} tone="error">
        {error}
      </FormMessage>
    </fieldset>
  );
};
